import { useState, useEffect } from "react";
import { Grid, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "const";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import styles from "./styles.module.css";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";
import * as PurchaseHelper from "helpers/PurchaseHelper";
import ConfirmPopup from "components/popups/Alert/ConfirmPopup";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import DateTimeHelpers from "helpers/DateTimeHelpers";

export default function PurchaseDetailsDialog({ open, onClose, purchase, onRefresh }) {
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detailedPurchase, setDetailedPurchase] = useState(purchase);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDeletePurchase = () => {
    setShowConfirm(true);
  };

  const onConfirmDelete = async () => {
    setLoading(true);
    try {
      await PurchaseHelper.deletePurchase(purchase.id);
      toast.success("Purchase deleted successfully");
      setShowConfirm(false);
      if (onRefresh) onRefresh();
      onClose();
    } catch (error) {
      console.error("Delete purchase error:", error);
      toast.error("An error occurred while deleting the purchase");
    } finally {
      setLoading(false);
    }
  };

  const handleEditPurchase = () => {
    navigate(ROUTES.EDIT_PURCHASE.replace(":purchaseId", purchase.id));
    onClose();
  };

  useEffect(() => {
    if (open && purchase?.id && (!purchase.items || purchase.items.length === 0)) {
      const fetchDetails = async () => {
        setLoading(true);
        try {
          const data = await PurchaseHelper.getLocalPurchase(purchase.id);
          if (data) {
            setDetailedPurchase(data);
          }
        } catch (error) {
          console.error("Failed to fetch local purchase details:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchDetails();
    } else if (open && purchase) {
      setDetailedPurchase(purchase);
    }
  }, [open, purchase]);

  if (!open) return null;

  return (
    <div className={styles.popUp} onClick={handleBackdropClick}>
      <Grid container className={styles.billContentWrapper} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <Grid item xs={12} className={styles.headSec} mb={2}>
          <h3>Purchase Details</h3>
          <CloseIcon className={styles.icon} onClick={onClose} />
        </Grid>

        {!detailedPurchase || loading ? (
          <Grid item xs={12} p={2}>
            <SkeletonLoading isOpen={true} count={12} height={50} xs={12} />
          </Grid>
        ) : (
          <>
            {/* Meta info section */}
            <Grid item xs={12} className={styles.headInfoSec}>
              <Grid container>
                <Grid item xs={6}>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Invoice No</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{detailedPurchase.invoiceNo || "-"}</div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Date</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>
                      {DateTimeHelpers.formatDateTime(detailedPurchase.date || "-")}
                    </div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Vendor</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{detailedPurchase.vendor?.name || "-"}</div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Mobile</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{detailedPurchase.vendor?.mobile || "-"}</div>
                  </div>
                </Grid>

                <Grid item xs={6}>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Total Amount</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>
                      {NumberFormatHelper.roundOff(detailedPurchase.totalPurchaseAmount)}
                    </div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Paid Amount</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{NumberFormatHelper.roundOff(detailedPurchase.paidAmount)}</div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Items Count</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{detailedPurchase.items?.length || 0}</div>
                  </div>
                </Grid>
              </Grid>
            </Grid>

            {/* Items Table */}
            <Grid item xs={12} className={styles.tableContainer}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ width: "18%" }}>PLU</th>
                    <th style={{ width: "37%" }}>Item Name</th>
                    <th style={{ width: "10%" }}>Qty</th>
                    <th style={{ width: "20%" }}>Rate</th>
                    <th style={{ width: "20%" }}>Margin(%)</th>
                    <th style={{ width: "25%" }}>Selling Price</th>
                    <th style={{ width: "16%" }}>Tax</th>
                    <th style={{ width: "20%" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(detailedPurchase.items || []).map((item, index) => (
                    <tr key={index}>
                      <td style={{ width: "18%" }}>{item.plu}</td>
                      <td style={{ width: "37%" }}>{item.item?.name || "-"}</td>
                      <td style={{ width: "10%" }}>{item.quantity || 0}</td>
                      <td style={{ width: "20%" }}>{NumberFormatHelper.roundOff(item.cost)}</td>
                      <td style={{ width: "20%" }}>{item.profit_margin}</td>
                      <td style={{ width: "25%" }}>{NumberFormatHelper.roundOff(item.price)}</td>
                      <td style={{ width: "16%" }}>{NumberFormatHelper.roundOff(item.tax)}</td>
                      <td style={{ width: "20%" }}>{NumberFormatHelper.roundOff(item.total)}</td>
                    </tr>
                  ))}
                  {(!detailedPurchase.items || detailedPurchase.items.length === 0) && (
                    <tr>
                      <td colSpan="6" style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                        No items found in this purchase
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Grid>

            <div className={styles.border}></div>

            <Grid item xs={12} className={styles.footSec} mt={2}>
              <Button className={styles.buttonGray} onClick={handleDeletePurchase}>
                Delete purchase
              </Button>
              <Button className={styles.buttonGray} onClick={handleEditPurchase}>
                Edit purchase
              </Button>
            </Grid>
          </>
        )}
      </Grid>
      <ConfirmPopup
        data={showConfirm}
        handleClose={() => setShowConfirm(false)}
        handleOkay={onConfirmDelete}
        alertTitle="Delete Purchase"
        alertContent="Are you sure you want to delete this purchase? This action cannot be undone."
        successBtnName="Delete"
      />
    </div>
  );
}
